/*
 * Lucid: Preis in der Sticky-Kaufleiste an die Abo-Auswahl koppeln.
 *
 * Das Problem, das dieses File loest: Auf der PDP ist der Appstle-Selling-Plan
 * (im Widget „Focus Starterplan", Gruppe „Focus Abo", -20 %) vorausgewaehlt.
 * Das Abo-Widget ist ein App-Embed und schreibt sein
 * <input name="selling_plan"> erst im Browser in das Produktformular - Liquid
 * sieht die Auswahl nie. Die Sticky-Leiste rendert deshalb serverseitig den
 * Einmalpreis (12,99 EUR), waehrend ihr
 * Button den echten Add-to-Cart-Button fernsteuert (siehe
 * assets/sticky-add-to-cart.js) und damit den Abo-Preis (10,39 EUR) in den
 * Warenkorb legt. Der Kunde sieht im Warenkorb also einen anderen Preis, als
 * der Button versprochen hat.
 *
 * Loesung: sections/product-information.liquid rendert eine Preis-Tabelle
 * (Variante -> Selling Plan -> formatierter Preis) als JSON in die Leiste.
 * Dieses Skript liest die aktuelle Selling-Plan-Auswahl aus dem Formular und
 * blendet den passenden Preis ein.
 *
 * Drei Dinge, die hier bewusst so geloest sind:
 *
 * 1) Klassisches Skript, kein Modul. Es haengt an keinem Theme-Import und
 *    laeuft damit auch dann, wenn Horizons Modul-Graph sich aendert.
 *
 * 2) Zusaetzlich zu den Events wird gepollt. Appstle setzt den Wert des
 *    versteckten Inputs per JavaScript (`el.value = ...`). Das erzeugt weder
 *    ein change-Event noch eine Attribut-Mutation - ein reiner Event- oder
 *    MutationObserver-Ansatz verpasst genau den vorausgewaehlten Startzustand.
 *    Das Intervall liest nur ein paar DOM-Properties und laeuft nur, solange
 *    das Element im Dokument haengt und der Tab sichtbar ist.
 *
 * 3) Verglichen wird gegen das gerenderte HTML, nicht gegen einen internen
 *    Merker. Grund: sticky-add-to-cart.js morpht die Leiste bei jedem
 *    Variantenwechsel gegen frisches Server-HTML und wirft unsere Ausgabe
 *    dabei weg. Der HTML-Vergleich stellt sie beim naechsten Tick wieder her,
 *    ohne dass wir den Morph-Zeitpunkt kennen muessen.
 *
 * Annahme, die ohne Storefront-Zugang nicht verifizierbar war: Appstle patcht
 * die Sticky-Leiste nicht selbst. Falls doch, gewinnt der letzte Schreiber -
 * dann diese Datei ersatzlos entfernen, statt beides parallel zu betreiben.
 */
(() => {
  const POLL_INTERVAL_MS = 500;
  const PLAN_FIELD = '[name="selling_plan"]';

  class LucidStickyPrice extends HTMLElement {
    /** @type {Record<string, Record<string, {price: string, compareAt: string, showCompare: boolean}>>} */
    #plans = {};
    /** @type {number | undefined} */
    #timer;
    /** @type {AbortController | undefined} */
    #abortController;

    connectedCallback() {
      this.#plans = this.#readPlans();
      // Produkt ohne Abo-Plan: nichts zu tun, kein Intervall, kein Listener.
      if (Object.keys(this.#plans).length === 0) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      // Sofort reagieren, wo es Events gibt; das Intervall ist nur das Netz
      // darunter (Appstle setzt den Wert teilweise lautlos).
      const section = this.closest('.shopify-section') ?? document;
      for (const type of ['change', 'input', 'click', 'variant:update']) {
        section.addEventListener(type, this.#scheduleApply, { signal });
      }

      this.#timer = window.setInterval(this.#apply, POLL_INTERVAL_MS);
      this.#apply();
    }

    disconnectedCallback() {
      if (this.#timer) window.clearInterval(this.#timer);
      this.#timer = undefined;
      this.#abortController?.abort();
      this.#abortController = undefined;
    }

    /**
     * Liest die serverseitig gerenderte Preis-Tabelle.
     * @returns {Record<string, Record<string, {price: string, compareAt: string, showCompare: boolean}>>}
     */
    #readPlans() {
      const script = this.querySelector('script[data-lucid-price-plans]');
      if (!script || !script.textContent) return {};
      try {
        return JSON.parse(script.textContent).plans ?? {};
      } catch {
        return {};
      }
    }

    #scheduleApply = () => {
      // Ein Frame warten: bei Klicks auf das Abo-Widget steht der neue Wert
      // erst nach dem Handler des Widgets im Formular.
      requestAnimationFrame(this.#apply);
    };

    /**
     * Findet das Produktformular, in dem Appstle sein Feld ablegt.
     * @returns {Element | null}
     */
    #getForm() {
      const section = this.closest('.shopify-section');
      if (!section) return null;
      return (
        section.querySelector('product-form-component form[data-type="add-to-cart-form"]') ??
        section.querySelector('product-form-component form') ??
        section.querySelector('form[data-type="add-to-cart-form"]')
      );
    }

    /**
     * Aktuell gewaehlte Selling-Plan-ID, oder null fuer Einmalkauf.
     * @returns {string | null}
     */
    #getSelectedPlanId() {
      const form = this.#getForm();
      if (!form) return null;

      const fields = form.querySelectorAll(PLAN_FIELD);
      for (const field of fields) {
        // Radios/Checkboxen: nur die angehakte zaehlt. Alles andere
        // (hidden, select) liefert direkt seinen Wert.
        if (field instanceof HTMLInputElement && (field.type === 'radio' || field.type === 'checkbox')) {
          if (!field.checked) continue;
        }
        const value = /** @type {HTMLInputElement} */ (field).value;
        if (value && value !== '0') return String(value);
      }
      return null;
    }

    /**
     * Aktuelle Varianten-ID. Quelle ist bewusst das <sticky-add-to-cart>-
     * Element: dessen data-current-variant-id wird von sticky-add-to-cart.js
     * gepflegt, so bleibt die Variantenlogik an einer Stelle.
     * @returns {string | null}
     */
    #getVariantId() {
      const host = this.closest('sticky-add-to-cart');
      const id = host instanceof HTMLElement ? host.dataset.currentVariantId : null;
      return id ? String(id) : null;
    }

    #apply = () => {
      if (document.hidden) return;

      const planTarget = this.querySelector('[data-lucid-price-plan]');
      const onceTarget = this.querySelector('[data-lucid-price-once]');
      if (!(planTarget instanceof HTMLElement) || !(onceTarget instanceof HTMLElement)) return;

      const variantId = this.#getVariantId();
      const planId = this.#getSelectedPlanId();
      const allocation = variantId && planId ? this.#plans[variantId]?.[planId] : undefined;

      const markup = allocation ? this.#renderAllocation(allocation) : '';

      // Nur schreiben, wenn sich etwas geaendert hat - sonst wuerde jeder
      // Intervall-Tick das DOM anfassen.
      if (planTarget.innerHTML !== markup) planTarget.innerHTML = markup;
      planTarget.toggleAttribute('hidden', markup === '');
      onceTarget.toggleAttribute('hidden', markup !== '');
    };

    /**
     * Baut die Preisanzeige fuer einen Selling Plan. Die Klassennamen sind
     * dieselben wie in snippets/price.liquid, damit Schriftgroesse,
     * Durchstreichung und die <390px-Regel (compare-at-price ausblenden)
     * ohne Zusatz-CSS greifen.
     *
     * Den Abstand zwischen den Betraegen macht CSS (column-gap auf
     * .lucid-sticky-price__plan, siehe sections/product-information.liquid) -
     * hier bewusst kein Leerzeichen im String: das faellt beim naechsten
     * Umbau lautlos weg und ist dann wieder „10,39 €12,99 €".
     * @param {{price: string, compareAt: string, showCompare: boolean}} allocation
     * @returns {string}
     */
    #renderAllocation(allocation) {
      const label = this.dataset.planLabel;
      const parts = [`<span class="price-item__group"><span class="price-item--sale price">${allocation.price}</span></span>`];

      if (allocation.showCompare && allocation.compareAt) {
        parts.push(
          `<span class="price-item__group"><span class="price-item--regular compare-at-price">${allocation.compareAt}</span></span>`
        );
      }
      if (label) {
        parts.push(`<span class="lucid-sticky-price__plan-label">${label}</span>`);
      }
      return parts.join('');
    }
  }

  if (!customElements.get('lucid-sticky-price')) {
    customElements.define('lucid-sticky-price', LucidStickyPrice);
  }
})();
